import { logEvent } from "src/utlis/analytics";
import { Badge, Button } from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { gql } from "src/__generated__";
import { useQuery } from "@apollo/client";
import { RequestFailed } from "src/components/RequestFailed";
import {
	ApplicationStatus,
	MyDiscardedApplicationsQuery,
	AllMyJobApplicationsQuery,
} from "src/__generated__/graphql";
import {
	ColumnDef,
	getCoreRowModel,
	getPaginationRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { ControlledDataTable } from "@/components/ui/controlled-data-table";
import { Skeleton } from "@chakra-ui/react";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button as UIButton } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { GET_MY_JOB_APPLICATIONS } from "src/features/jobs/graphql";
import { useNavigate } from "react-router-dom";

const MY_DISCARDED_JOB_APPLICATIONS = gql(`
	query MyDiscardedApplications {
  myDiscardedApplications {
    id
    ad {
      id
      title
			companyName
      organization {
        name
      }
    }
			createdAt
  }
}
`);

export const getStatusBadgeLabel = (status: ApplicationStatus): string => {
	switch (status) {
		case ApplicationStatus.Drafted:
			return "Drafted";
		case ApplicationStatus.Submitted:
			return "Submitted";
		case ApplicationStatus.Viewed:
			return "Viewed";
		case ApplicationStatus.Shortlisted:
			return "Shortlisted";
		case ApplicationStatus.Rejected:
			return "Rejected";
		case ApplicationStatus.Hired:
			return "Hired 🎉";
		case ApplicationStatus.OfferAccepted:
			return "Offer Accepted";
		case ApplicationStatus.OfferDeclined:
			return "Offer Declined";
		default:
			return status
				.replace("_", " ")
				.toLowerCase()
				.split(" ")
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(" ");
	}
};

const getBadgeColor = (status: string) => {
	switch (status) {
		case ApplicationStatus.Drafted:
		case ApplicationStatus.Shortlisted:
			return "#F5B700";
		case ApplicationStatus.Submitted:
		case ApplicationStatus.Viewed:
			return "#153243";
		case ApplicationStatus.Hired:
		case ApplicationStatus.OfferAccepted:
			return "#96F550";
		case ApplicationStatus.Rejected:
		case ApplicationStatus.OfferDeclined:
			return "red";
		default:
			return "gray";
	}
};

const jobApplicationsColumns: ColumnDef<
	AllMyJobApplicationsQuery["allMyJobApplications"][0]
>[] = [
	{
		accessorKey: "ad.title",
		header: "Title",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[14px] lg:text-[16px] text-gray-500">
				{row.original.ad?.title ||
					row.original.communityJobOpportunity?.title ||
					"-"}
			</div>
		),
	},
	{
		accessorKey: "ad.organization.name",
		header: "Organization",
		cell: ({ row }) => {
			const companyName =
				(row.original.ad?.companyName ??
					row.original.communityJobOpportunity?.organization.name) ||
				"-";
			return (
				<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
					{companyName}
				</div>
			);
		},
	},
	{
		accessorKey: "status",
		header: "Status",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				<Badge
					backgroundColor={getBadgeColor(row.original.status)}
					color={"#FEFEFE"}
					variant="solid"
					rounded={"md"}
					textTransform={"none"}
					px={1.5}
				>
					{row.original.enhancementRequested
						? "Reapplication requested"
						: getStatusBadgeLabel(row.original.status)}
				</Badge>
			</div>
		),
	},
	{
		accessorKey: "createdAt",
		header: "Date",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{new Date(row.original.createdAt).toISOString().split("T")[0]}
			</div>
		),
	},
	{
		id: "actions",
		header: "",
		cell: ({ row }) => {
			if (!row.original.enhancementRequested) return null;

			const ad = row.original.ad;

			// at this moment enhancement requested is only for recruiter job applications
			if (!ad) return null;

			return (
				<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
					<Button
						colorScheme="brand"
						py={{
							base: 1,
							lg: 2,
						}}
						px={2}
						fontSize={{
							base: "13px",
							lg: "15px",
						}}
						className="bg-flatteredFlamingo-700 w-full md:w-fit"
						onClick={() => {
							window.open(
								`${process.env.REACT_APP_HOME_APP_URL}/jobs/${ad.id}/apply`,
								"_blank"
							);
						}}
					>
						Reapply
					</Button>
				</div>
			);
		},
	},
];

const draftApplicationsColumns: ColumnDef<
	MyDiscardedApplicationsQuery["myDiscardedApplications"][0]
>[] = [
	{
		accessorKey: "ad.title",
		header: "Title",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[14px] lg:text-[16px] text-gray-500">
				{row.original.ad.title}
			</div>
		),
	},
	{
		accessorKey: "ad.companyName",
		header: "Employer",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{row.original.ad.companyName}
			</div>
		),
	},
	{
		accessorKey: "status",
		header: "Status",
		cell: () => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				<Badge
					backgroundColor={getBadgeColor(ApplicationStatus.Drafted)}
					color={"#FEFEFE"}
					variant="solid"
					rounded={"md"}
					textTransform={"none"}
					px={1.5}
				>
					{getStatusBadgeLabel(ApplicationStatus.Drafted)}
				</Badge>
			</div>
		),
	},
	{
		accessorKey: "createdAt",
		header: "Date",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				{new Date(row.original.createdAt).toISOString().split("T")[0]}
			</div>
		),
	},
	{
		id: "actions",
		header: "",
		cell: ({ row }) => (
			<div className="whitespace-nowrap py-2.5 text-[13px] lg:text-[16px] text-gray-500">
				<Button
					bgColor={"#ED6E5C"}
					color={"#FEFEFE"}
					_hover={{
						bgColor: "#FBE2DE",
						color: "#F08677",
					}}
					py={{ base: 1, lg: 2 }}
					px={2}
					fontSize={{ base: "13px", lg: "15px" }}
					className="bg-flatteredFlamingo-700 w-full md:w-fit"
					onClick={() => {
						window.open(
							`${process.env.REACT_APP_HOME_APP_URL}/jobs/${row.original.ad.id}/apply`,
							"_blank"
						);
					}}
				>
					Complete now
				</Button>
			</div>
		),
	},
];

export default function MyJobApplications() {
	const navigate = useNavigate();

	const {
		loading: loadingJobApplications,
		data: jobApplicationsData,
		error: jobApplicationsError,
	} = useQuery(GET_MY_JOB_APPLICATIONS, {
		fetchPolicy: "network-only",
	});

	const {
		loading: loadingDiscardedApplications,
		data: discardedApplicationsData,
		error: discardedApplicationsError,
	} = useQuery(MY_DISCARDED_JOB_APPLICATIONS, {
		fetchPolicy: "network-only",
	});

	const applications = jobApplicationsData?.allMyJobApplications || [];
	const discardedApplications =
		discardedApplicationsData?.myDiscardedApplications || [];

	const jobApplicationsTable = useReactTable({
		data: applications,
		columns: jobApplicationsColumns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});

	const draftApplicationsTable = useReactTable({
		data: discardedApplications,
		columns: draftApplicationsColumns,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: {
			pagination: {
				pageSize: 10,
			},
		},
	});

	if (jobApplicationsError) {
		return <RequestFailed text={jobApplicationsError.message} />;
	}

	if (discardedApplicationsError) {
		return <RequestFailed text={discardedApplicationsError.message} />;
	}

	return (
		<>
			<Helmet>
				<title>LaborHack | My Job Applications</title>
			</Helmet>
			<main className="flex flex-col gap-8">
				<div className="flex flex-col gap-3 lg:gap-6">
					<div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-4 border-b pb-2">
						<h1 className="text-[18px] lg:text-[23px] font-semibold">
							My job applications
						</h1>
						<div className="flex gap-4 flex-wrap md:flex-nowrap items-center">
							{/* <Button
									colorScheme="brand"
									py={{
										base: 2,
										lg: 4,
									}}
									px={{
										base: 2,
										lg: 4,
									}}
									fontSize={{
										base: "14px",
										lg: "16px",
									}}
									variant={"outline"}
									onClick={() => {
										navigate("/my-job-applications/notification-preferences");
									}}
								>
									Notification preferences
								</Button> */}
							<Button
								bgColor={"#ED6E5C"}
								color={"#FEFEFE"}
								_hover={{
									bgColor: "#FBE2DE",
									color: "#F08677",
								}}
								py={{
									base: 2,
									lg: 4,
								}}
								px={{
									base: 2,
									lg: 4,
								}}
								fontSize={{
									base: "14px",
									lg: "16px",
								}}
								onClick={() => {
									logEvent("pro_dashboard_view_jobs_click");

									navigate("/opportunities");
								}}
							>
								View jobs
							</Button>
						</div>
					</div>
					<div className="">
						<div className="mt-8 flow-root">
							{loadingJobApplications ? (
								<div className="rounded-md border">
									<div className="p-4 space-y-4">
										{/* Header skeleton */}
										<div className="flex border-b pb-4">
											{[1, 2, 3, 4, 5, 6].map((i) => (
												<Skeleton key={i} className="h-5 w-1/6 mr-4" />
											))}
										</div>
										{/* Rows skeleton */}
										{Array(5)
											.fill(0)
											.map((_, index) => (
												<div key={index} className="flex py-3">
													{[1, 2, 3, 4, 5, 6].map((i) => (
														<Skeleton key={i} className="h-5 w-1/6 mr-4" />
													))}
												</div>
											))}
									</div>
								</div>
							) : (
								<>
									<ControlledDataTable table={jobApplicationsTable} />
									<div className="flex flex-col">
										<Separator />
										<div className="w-full min-h-16">
											<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 py-4">
												<p className="flex items-center gap-2 text-sm text-gray-500">
													{jobApplicationsTable.getRowCount() === 0 ? (
														"Showing no applications"
													) : (
														<>
															Showing
															<span className="text-sm text-[#121212]">
																{jobApplicationsTable.getState().pagination
																	.pageIndex *
																	jobApplicationsTable.getState().pagination
																		.pageSize +
																	1}
																-
																{Math.min(
																	(jobApplicationsTable.getState().pagination
																		.pageIndex +
																		1) *
																		jobApplicationsTable.getState().pagination
																			.pageSize,
																	jobApplicationsTable.getFilteredRowModel()
																		.rows.length
																)}
															</span>
															of{" "}
															<span className="text-sm text-[#121212]">
																{jobApplicationsTable.getRowCount()}
															</span>
															applications
														</>
													)}
												</p>
												<div className="flex items-center gap-2">
													<div className="flex items-center space-x-2">
														<UIButton
															className={cn(
																"rounded-full px-[9px] border-[#212121]",
																!jobApplicationsTable.getCanPreviousPage() &&
																	"border-[#D4D4D4]"
															)}
															variant="outline"
															size="sm"
															onClick={() =>
																jobApplicationsTable.previousPage()
															}
															disabled={
																!jobApplicationsTable.getCanPreviousPage()
															}
														>
															<ChevronLeft className="w-4 h-4" />
														</UIButton>
														<UIButton
															className={cn(
																"rounded-full px-[9px] border-[#212121]",
																!jobApplicationsTable.getCanNextPage() &&
																	"border-[#D4D4D4]"
															)}
															variant="outline"
															size="sm"
															onClick={() => jobApplicationsTable.nextPage()}
															disabled={!jobApplicationsTable.getCanNextPage()}
														>
															<ChevronRight className="w-4 h-4" />
														</UIButton>
													</div>
													<Select
														value={String(
															jobApplicationsTable.getState().pagination
																.pageSize
														)}
														onValueChange={(value) => {
															jobApplicationsTable.setPageSize(Number(value));
														}}
													>
														<SelectTrigger className="w-[70px]">
															<SelectValue placeholder="10" />
														</SelectTrigger>
														<SelectContent>
															{[5, 10, 20, 50].map((pageSize) => (
																<SelectItem
																	key={pageSize}
																	value={String(pageSize)}
																>
																	{pageSize}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											</div>
										</div>
									</div>
								</>
							)}
						</div>
					</div>

					{/* Draft Applications Section */}
					{!loadingDiscardedApplications &&
						discardedApplications.length > 0 && (
							<div>
								<div className="mt-8 flow-root">
									<h2 className="text-[16px] lg:text-[20px] font-semibold mb-4">
										Draft Applications
									</h2>
									<ControlledDataTable table={draftApplicationsTable} />
									<div className="flex flex-col">
										<Separator />
										<div className="w-full min-h-16">
											<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 py-4">
												<p className="flex items-center gap-2 text-sm text-gray-500">
													{draftApplicationsTable.getRowCount() === 0 ? (
														"Showing no draft applications"
													) : (
														<>
															Showing
															<span className="text-sm text-[#121212]">
																{draftApplicationsTable.getState().pagination
																	.pageIndex *
																	draftApplicationsTable.getState().pagination
																		.pageSize +
																	1}
																-
																{Math.min(
																	(draftApplicationsTable.getState().pagination
																		.pageIndex +
																		1) *
																		draftApplicationsTable.getState().pagination
																			.pageSize,
																	draftApplicationsTable.getFilteredRowModel()
																		.rows.length
																)}
															</span>
															of{" "}
															<span className="text-sm text-[#121212]">
																{draftApplicationsTable.getRowCount()}
															</span>
															draft applications
														</>
													)}
												</p>
												<div className="flex items-center gap-2">
													<div className="flex items-center space-x-2">
														<UIButton
															className={cn(
																"rounded-full px-[9px] border-[#212121]",
																!draftApplicationsTable.getCanPreviousPage() &&
																	"border-[#D4D4D4]"
															)}
															variant="outline"
															size="sm"
															onClick={() =>
																draftApplicationsTable.previousPage()
															}
															disabled={
																!draftApplicationsTable.getCanPreviousPage()
															}
														>
															<ChevronLeft className="w-4 h-4" />
														</UIButton>
														<UIButton
															className={cn(
																"rounded-full px-[9px] border-[#212121]",
																!draftApplicationsTable.getCanNextPage() &&
																	"border-[#D4D4D4]"
															)}
															variant="outline"
															size="sm"
															onClick={() => draftApplicationsTable.nextPage()}
															disabled={
																!draftApplicationsTable.getCanNextPage()
															}
														>
															<ChevronRight className="w-4 h-4" />
														</UIButton>
													</div>
													<Select
														value={String(
															draftApplicationsTable.getState().pagination
																.pageSize
														)}
														onValueChange={(value) => {
															draftApplicationsTable.setPageSize(Number(value));
														}}
													>
														<SelectTrigger className="w-[70px]">
															<SelectValue placeholder="10" />
														</SelectTrigger>
														<SelectContent>
															{[5, 10, 20, 50].map((pageSize) => (
																<SelectItem
																	key={pageSize}
																	value={String(pageSize)}
																>
																	{pageSize}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}
				</div>
			</main>
		</>
	);
}
